import { useEffect, useRef, useState, useCallback } from 'react';
import { Sparkles, Camera, Wand2, Upload } from 'lucide-react';
import { useEditorStore } from '../../stores/editorStore';
import { useProjectStore } from '../../stores/projectStore';
import { useImproveNarration, useGenerateImage, useUpdateShot } from '../../hooks';
import { Button } from '../atoms/Button';
import { Input } from '../../components/atoms/Input';
import { Textarea } from '../../components/atoms/Textarea';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import type { SceneWithShots } from '../../types/database';

interface ShotEditorProps {
  scenes: SceneWithShots[];
}

export function ShotEditor({ scenes }: ShotEditorProps) {
  const {
    manualSceneIdx,
    manualShotIdx,
    localNarration,
    setLocalNarration,
    isGenerating,
    setIsGenerating,
  } = useEditorStore();

  const { updateShot, saveShot } = useProjectStore();
  const improveNarrationMutation = useImproveNarration();
  const generateImageMutation = useGenerateImage();
  const updateShotMutation = useUpdateShot();
  const { currentProject } = useProjectStore();

  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentScene = scenes[manualSceneIdx];
  const currentShot = currentScene?.shots?.[manualShotIdx];

  // Refs to persist narration to DB when navigating away from a shot
  const prevShotRef = useRef<{ sceneId: string; shotId: string } | null>(null);
  const localNarrationRef = useRef(localNarration);
  localNarrationRef.current = localNarration;

  // Sync local state when shot changes, save outgoing narration to DB
  useEffect(() => {
    // Save previous shot's narration to DB before loading new shot
    if (prevShotRef.current) {
      saveShot(prevShotRef.current.sceneId, prevShotRef.current.shotId, {
        narration_text: localNarrationRef.current,
      });
    }

    if (currentShot) {
      setLocalNarration(currentShot.narration_text || '');
      prevShotRef.current = { sceneId: currentScene.id, shotId: currentShot.id };
    }
  }, [currentShot?.id, setLocalNarration]);

  const handleNarrationChange = (narration: string) => {
    setLocalNarration(narration);
    if (currentShot) {
      updateShot(currentScene.id, currentShot.id, { narration_text: narration });
    }
  };

  const handleNarrationBlur = () => {
    if (currentShot) {
      saveShot(currentScene.id, currentShot.id, { narration_text: localNarration });
    }
  };

  const handleNameChange = (name: string) => {
    if (currentShot) {
      saveShot(currentScene.id, currentShot.id, { name });
    }
  };

  const handleTextChange = (text: string) => {
    if (currentShot) {
      saveShot(currentScene.id, currentShot.id, { text });
    }
  };

  const handleTextEnabledChange = (enabled: boolean) => {
    if (currentShot) {
      saveShot(currentScene.id, currentShot.id, { text_enabled: enabled });
    }
  };

  const handleDurationChange = (duration: string) => {
    if (currentShot) {
      const durationSec = parseInt(duration) || 5;
      saveShot(currentScene.id, currentShot.id, { duration_sec: durationSec });
    }
  };

  const handleAiPromptChange = (aiPrompt: string) => {
    if (currentShot) {
      saveShot(currentScene.id, currentShot.id, { ai_prompt: aiPrompt });
    }
  };

  const handleImageUpload = useCallback(async (file: File) => {
    if (!currentShot || !file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Samo slike su dozvoljene');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Slika ne smije biti veća od 10MB');
      return;
    }

    setIsUploading(true);

    try {
      const ext = file.name.split('.').pop() || 'png';
      const fileName = `uploaded-${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;

      const { data, error } = await supabase.storage
        .from('images')
        .upload(fileName, file, {
          contentType: file.type,
          upsert: false,
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(fileName);

      saveShot(currentScene.id, currentShot.id, {
        visual_type: 'image',
        media_url: publicUrl,
        thumbnail_url: publicUrl,
      });
      toast.success('Slika uploadana!');
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Greška pri uploadu slike');
    } finally {
      setIsUploading(false);
    }
  }, [currentShot, currentScene, saveShot]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageUpload(file);
  }, [handleImageUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImageUpload(file);
    e.target.value = '';
  }, [handleImageUpload]);

  const handleImproveNarration = async () => {
    if (!localNarration) {
      toast.error('Unesite tekst naracije za poboljšanje');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const result = await improveNarrationMutation.mutateAsync({
        narration: localNarration,
        tone: 'professional',
        language: 'Croatian',
      });
      
      setLocalNarration(result.improved);
      saveShot(currentScene.id, currentShot.id, { narration_text: result.improved });
      toast.success('Naracija uspješno poboljšana!');
    } catch (error) {
      console.error('Failed to improve narration:', error);
      toast.error('Greška pri poboljšanju naracije');
      
      // Fallback: simulate improvement
      const improved = `${currentShot.narration_text} (poboljšano)`;
      setLocalNarration(improved);
      saveShot(currentScene.id, currentShot.id, { narration_text: improved });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!currentShot?.ai_prompt) {
      toast.error('Unesite AI prompt za generaciju slike');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const result = await generateImageMutation.mutateAsync({
        prompt: currentShot.ai_prompt,
        aspectRatio: '16:9',
      });
      
      // Use the actual generated image from API response
      const { finalImageUrl, prompt: enhancedPrompt, aspectRatio, model } = result;
      
      saveShot(currentScene.id, currentShot.id, {
        visual_type: 'image',
        media_url: finalImageUrl,
        thumbnail_url: finalImageUrl,
      });
      toast.success('Slika generirana!');
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error('Greška pri generaciji slike');
      
      // Fallback: use placeholder
      saveShot(currentScene.id, currentShot.id, {
        visual_type: 'image',
        media_url: `https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80`,
        thumbnail_url: `https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80`,
      });
    } finally {
      setIsGenerating(false);
    }
  };



  if (!currentShot) {
    return (
      <div className="bg-white/5 rounded-[2.5rem] border border-white/10 p-6 flex items-center justify-center h-full">
        <p className="text-slate-500">Nema odabranog kadra</p>
      </div>
    );
  }

  return (
    <div className="bg-white/5 rounded-[2.5rem] border border-white/10 p-6 flex flex-col h-full gap-5">
      {/* Box Title */}
      <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400">
        Postavke Kadra
      </h3>

      {/* Text Field with Enable Checkbox */}
      {currentShot.text_enabled && (
        <div className="flex gap-4 items-start">
          <div className="flex-1">
            <Input
              label="Text"
              value={currentShot.text || ''}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder="Unesite tekst za prikaz..."
            />
          </div>
        </div>
      )}
      <div className="flex justify-start">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={currentShot.text_enabled}
            onChange={(e) => handleTextEnabledChange(e.target.checked)}
            className="w-5 h-5 rounded border-white/20 bg-black/40 text-blue-500 focus:ring-blue-500/50"
          />
          <span className="text-[11px] font-medium text-slate-400"> Omogući Tekst</span>
        </label>
      </div>

      {/* Narration */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <label className="text-[11px] font-bold text-blue-500 uppercase tracking-widest">
            Naracija
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleImproveNarration}
            isLoading={isGenerating}
            leftIcon={<Wand2 size={10} />}
            className="text-[9px] font-black text-blue-400 bg-blue-500/10 px-2 py-1 h-auto"
          >
            AI NARACIJA
          </Button>
        </div>
        <Textarea
          value={localNarration}
          onChange={(e) => handleNarrationChange(e.target.value)}
          onBlur={handleNarrationBlur}
          rows={3}
          placeholder="Unesite tekst naracije..."
        />
      </div>

      {/* AI Image Generation */}
      <div className="space-y-3">
        <div className="p-4 bg-black/20 rounded-2xl border border-white/5 space-y-4">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest block">
            AI PROMPT
          </label>
          <textarea
            value={currentShot.ai_prompt || ''}
            onChange={(e) => handleAiPromptChange(e.target.value)}
            placeholder="Opišite vizual koji želite generirati..."
            className="w-full bg-black/40 border-none rounded-xl p-3 text-[11px] font-mono text-slate-400 min-h-[60px] outline-none resize-none"
          />

          <Button
            variant="secondary"
            onClick={handleGenerateImage}
            isLoading={isGenerating}
            leftIcon={<Sparkles size={12} />}
            className="w-full text-[10px]"
          >
            Generiraj Sliku s AI
          </Button>

          {/* Image Upload / Drag & Drop */}
          <div className="border-t border-white/5 pt-4">
            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest block mb-3">
              ILI UPLOADAJ SLIKU
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`w-full border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all ${
                isDragOver
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-white/10 hover:border-white/20 hover:bg-white/5'
              }`}
            >
              {isUploading ? (
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Upload size={20} className={isDragOver ? 'text-blue-400' : 'text-slate-500'} />
              )}
              <p className="text-[10px] text-slate-500 text-center">
                {isUploading
                  ? 'Uploadam...'
                  : isDragOver
                    ? 'Ispustite sliku ovdje'
                    : 'Povucite sliku ovdje ili kliknite za odabir'}
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
